import { useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import { Modal } from 'react-bootstrap';
import Button, { ButtonGroup, LoaderButton } from '../../../../generic/Button';
import { selectOptionFactory } from '../../../../../utils';
import { useAddUsersToTeam, useUsersFromOrg } from '../../../../../hooks/api';
import { window } from '../../../../../globals';
import useSelectRoles from '../hooks/useSelectRoles';
import { FormGroup, FormLabelVariant, LabeledSelect } from '../../../../generic';
import type { SelectOption } from '../../../../../types';

const getCurrentOrgId = () => window.user?.organizationId ?? 0;

const BUILT_IN_TEAM_VIEWER_ROLE_ID = 6;

type Props = {
  teamId: number;
  teamName: string;
  //  This is a Set of all the users who are already on the team.
  teamUserIds: Set<number>;
  onHide: () => void;
};

/**
 * The modal for adding users to a team. It will fetch the users (using the deprecated useUsersFromOrg hook)
 * and the roles (using the new `useSelectRoles` hook). The user will then be able to select the users they want to add
 * and the default role they want to set for them on the team.
 *
 * @param {Props} props - The props for the component.
 * @param {number} props.teamId - The ID of the team to add users to.
 * @param {string} props.teamName - The name of the team to add users to.
 * @param {Set<number>} props.teamUserIds - The IDs of the users who are already on the team.
 * @param {() => void} props.onHide - The callback to hide the modal.
 * @returns {VoidFunctionComponent} The component.
 */
const AddMembersModal: VoidFunctionComponent<Props> = ({ teamId, teamName, onHide, teamUserIds }) => {
  //  State:
  const [selectedRole, setSelectedRole] = useState<SelectOption<number> | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<Array<SelectOption<number>>>([]);
  //  Data fetching
  const { users, isLoading: isLoadingOrgUsers } = useUsersFromOrg(getCurrentOrgId());
  const { roleOptions, isLoading: isLoadingRoles } = useSelectRoles(BUILT_IN_TEAM_VIEWER_ROLE_ID);

  const isLoadingAny = isLoadingOrgUsers || isLoadingRoles;

  //  Filter out any users who are already on the team, and generate a SelectOption for each user.
  const userOptions: Array<SelectOption<number>> = useMemo(() =>
    users
      ?.filter(user => !teamUserIds.has(user.id))
      .map(user => selectOptionFactory(user.id, user.username || user.email)) ?? [],
  [users, teamUserIds]);

  const { mutateAsync: addUsersToTeam, isLoading: isAddingUsersToTeam } = useAddUsersToTeam();

  const handleSave = async () => {
    await addUsersToTeam({
      teamId,
      users: selectedUsers.map(user => ({ id: user.value, roleId: selectedRole?.value })),
    });
    onHide();
    //  Reset the state:
    setSelectedRole(null);
    setSelectedUsers([]);
  };

  return (
    <Modal show onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title className="margin-bottom-sm">
          Add Members to Team &quot;{teamName}&quot;
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormGroup>
          <LabeledSelect
            label="Select Users"
            id="users"
            options={userOptions}
            onChange={setSelectedUsers}
            value={selectedUsers}
            variant={FormLabelVariant.Filter}
            isDisabled={isLoadingOrgUsers}
            placeholder="Select users to add to this team"
            isMulti
          />
          <LabeledSelect
            label="Team Role"
            id="defaultRoleId"
            options={roleOptions}
            onChange={setSelectedRole}
            value={selectedRole}
            variant={FormLabelVariant.Filter}
            isDisabled={isLoadingOrgUsers}
            placeholder="Select the role for these users on the team"
          />
        </FormGroup>
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup>
          <Button
            type="secondary"
            size="medium"
            onClick={onHide}
            isDisabled={isLoadingAny}
          >
            Cancel
          </Button>
          <LoaderButton
            type="primary"
            size="medium"
            onClick={handleSave}
            isDisabled={isLoadingAny || selectedUsers.length === 0 || !selectedRole}
            isLoading={isAddingUsersToTeam}
          >
            {selectedUsers.length > 0 ? `Add ${selectedUsers.length} Users` : 'Add Users'}
          </LoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default AddMembersModal;
